import * as React from "react";

import { InlineEdit } from "@/shared/components/EntityPage/InlineEdit";
import { useNotification } from "@/shared/components/notifications/useNotification";
import type { MetricDefinitionRecord } from "@/shared/domain/metrics";
import { Tooltip } from "@/shared/foundation/primitives";
import type { ICellRendererParams } from "@/shared/foundation/primitives/Table";
import { RobotoDomainException } from "@/shared/http";

import {
  DefinitionsTableContext,
  type DefinitionsTableActions,
} from "./definitionsTableContext";
import styles from "./MetricDefinitionsTable.module.css";
import { UnitEditor } from "./UnitEditor";

function useDefinitionsTable(): DefinitionsTableActions {
  const actions = React.useContext(DefinitionsTableContext);
  if (actions === null) {
    throw new Error("Definition cells must render inside the table's context");
  }
  return actions;
}

type CellParams = ICellRendererParams<MetricDefinitionRecord>;

/** Why a save of `field` failed: the server's reason when it refused it. */
function saveFailedMessage(
  field: "description" | "unit",
  name: string,
  error: unknown,
): string {
  return error instanceof RobotoDomainException && error.isClientError()
    ? `Couldn't save the ${field} of ${name}: ${error.message}`
    : `Couldn't save the ${field} of ${name}. Try again.`;
}

/** The definition's name, in full in a tooltip when the column cuts it off. */
export function NameCell({ data: definition }: CellParams) {
  if (!definition) {
    return null;
  }
  return (
    <Tooltip>
      <Tooltip.Trigger
        render={<span className={styles.name}>{definition.name}</span>}
      />
      <Tooltip.Content>{definition.name}</Tooltip.Content>
    </Tooltip>
  );
}

/**
 * The description, one line. A click edits it in place, empty or not: Enter
 * or clicking away saves, Esc cancels.
 */
export function DescriptionCell({ data: definition }: CellParams) {
  const { editing, startEditing, stopEditing, isEditing, save } =
    useDefinitionsTable();
  const showNotification = useNotification();
  if (!definition) {
    return null;
  }
  const description = definition.description ?? "";
  if (editing?.name === definition.name && editing.field === "description") {
    const edit = editing;
    return (
      <div className={styles.editCell}>
        <InlineEdit
          value={description}
          onSave={(next) =>
            save(definition, { description: next === "" ? null : next }).catch(
              (error: unknown) => {
                // An open editor shows the failure itself, with Retry. Once
                // it has closed, the cell is back to the old description
                // and only a notification can say why.
                if (!isEditing(edit)) {
                  showNotification(
                    saveFailedMessage("description", definition.name, error),
                    "error",
                  );
                }
                throw error;
              },
            )
          }
          textStyle="body"
          ariaLabel={`description of ${definition.name}`}
          placeholder="Add a description"
          editing
          onEditingChange={(open) => {
            if (!open) {
              stopEditing(edit);
            }
          }}
          fill
        />
      </div>
    );
  }
  return (
    <button
      type="button"
      className={styles.cellButton}
      aria-label={`Edit description of ${definition.name}${description ? `: ${description}` : ""}`}
      title={description || undefined}
      onClick={() =>
        startEditing({ name: definition.name, field: "description" })
      }
    >
      <span className={styles.description}>{description}</span>
    </button>
  );
}

/**
 * The unit. A click, empty or not, edits it with a list of the units the
 * org uses.
 */
export function UnitCell({ data: definition }: CellParams) {
  const { editing, startEditing, stopEditing, save, unitCounts } =
    useDefinitionsTable();
  const showNotification = useNotification();
  const [saving, setSaving] = React.useState(false);
  if (!definition) {
    return null;
  }
  if (editing?.name === definition.name && editing.field === "unit") {
    const edit = editing;
    return (
      <div className={styles.editCell}>
        <UnitEditor
          name={definition.name}
          unit={definition.unit}
          unitCounts={unitCounts}
          saving={saving}
          onDone={(unit) => {
            if (unit === undefined || unit === definition.unit) {
              stopEditing(edit);
              return;
            }
            setSaving(true);
            // A failed save puts the old unit back and says why.
            save(definition, { unit })
              .catch((error: unknown) =>
                showNotification(
                  saveFailedMessage("unit", definition.name, error),
                  "error",
                ),
              )
              .finally(() => {
                setSaving(false);
                // The cell may since have closed, or another opened.
                stopEditing(edit);
              });
          }}
        />
      </div>
    );
  }
  return (
    <button
      type="button"
      className={styles.cellButton}
      aria-label={`Edit unit of ${definition.name}${definition.unit ? `: ${definition.unit}` : ""}`}
      onClick={() => startEditing({ name: definition.name, field: "unit" })}
    >
      <span className={styles.unit}>{definition.unit}</span>
    </button>
  );
}
